/**
 * PackageEditor — левая колонка правки клиники: таблица пакетов
 * (название/до N/цена, удаление), строка «11+ за место», «+ пакет», строка
 * валидации и кнопки Отмена/Сохранить. Состояние черновика — в ClinicEditor.
 */

import {Check, CircleAlert, Plus, Trash2} from 'lucide-react';

import {Button} from '@components/ui/Button';
import {formatNumber} from '@utils/format';

import {type PackageDraft, type Validation} from '../../Tariffs.utils';

type PackageEditorProps = {
  drafts: PackageDraft[];
  overPrice: string;
  setOverPrice: (v: string) => void;
  onPatch: (id: string, part: Partial<PackageDraft>) => void;
  onRemove: (id: string) => void;
  onAdd: () => void;
  valid: Validation;
  onCancel: () => void;
  onSave: () => void;
};

export const PackageEditor = ({
  drafts,
  overPrice,
  setOverPrice,
  onPatch,
  onRemove,
  onAdd,
  valid,
  onCancel,
  onSave,
}: PackageEditorProps) => (
  <div>
    <div className="overflow-hidden rounded-xl border border-skr-line2">
      <div className="grid grid-cols-[1.1fr_0.9fr_0.9fr_40px] border-b border-skr-line2 bg-skr-head px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-[.04em] text-skr-label">
        <div>Название</div>
        <div>Врачей</div>
        <div>Цена</div>
        <div />
      </div>

      {drafts.map((d) => (
        <div
          key={d.id}
          className="grid grid-cols-[1.1fr_0.9fr_0.9fr_40px] items-center border-b border-[#F3F4F6] px-3.5 py-[11px]"
        >
          <div className="pr-2.5">
            <input
              value={d.name}
              onChange={(e) => onPatch(d.id, {name: e.target.value})}
              className="h-[38px] w-full rounded-lg border border-input px-2.5 text-sm font-semibold outline-none focus:border-primary"
            />
          </div>
          <div className="flex items-center gap-[5px]">
            <span className="text-[12.5px] text-muted-foreground">до</span>
            <input
              value={String(d.upTo)}
              onChange={(e) =>
                onPatch(d.id, {upTo: Number(e.target.value.replace(/\D/g, '') || 0)})
              }
              inputMode="numeric"
              className="h-[38px] w-12 rounded-lg border border-input px-[9px] font-mono text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="flex items-center gap-[5px]">
            <input
              value={formatNumber(d.price)}
              onChange={(e) =>
                onPatch(d.id, {price: Number(e.target.value.replace(/\D/g, '') || 0)})
              }
              inputMode="numeric"
              className="h-[38px] w-[82px] rounded-lg border border-input px-[9px] font-mono text-sm outline-none focus:border-primary"
            />
            <span className="text-xs text-skr-faint">сом</span>
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => onRemove(d.id)}
              disabled={drafts.length <= 1}
              aria-label="Удалить пакет"
              className="grid size-8 place-items-center rounded-md text-skr-danger disabled:text-[#D2D5DB]"
            >
              <Trash2 className="size-4" strokeWidth={1.9} />
            </button>
          </div>
        </div>
      ))}

      <div className="grid grid-cols-[1.3fr_1fr_40px] items-center bg-skr-head px-3.5 py-[11px]">
        <div className="text-[13px] font-medium text-skr-ink">11+ · за каждое место</div>
        <div className="flex items-center gap-1.5">
          <input
            value={formatNumber(Number(overPrice || 0))}
            onChange={(e) => setOverPrice(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            className="h-[38px] w-24 rounded-lg border border-input px-2.5 font-mono text-sm outline-none focus:border-primary"
          />
          <span className="text-xs text-skr-faint">сом</span>
        </div>
        <div />
      </div>

      <div className="px-3.5 py-2.5">
        <button
          onClick={onAdd}
          className="inline-flex h-9 items-center gap-[7px] rounded-[9px] border border-dashed border-[#C9D0DA] bg-card px-[13px] text-[13px] font-medium text-primary"
        >
          <Plus className="size-[15px]" strokeWidth={2.2} />
          Добавить пакет
        </button>
      </div>
    </div>

    <div
      className={`mt-2.5 flex items-center gap-2 text-xs ${
        valid.ok ? 'text-skr-paid' : 'text-skr-danger'
      }`}
    >
      {valid.ok ? (
        <Check className="size-3.5" strokeWidth={2.4} />
      ) : (
        <CircleAlert className="size-3.5" strokeWidth={2} />
      )}
      {valid.message}
    </div>

    <div className="mt-3.5 flex gap-2.5">
      <Button variant="outline" className="h-[42px] px-4" onClick={onCancel}>
        Отмена
      </Button>
      <Button className="h-[42px] px-4" disabled={!valid.ok} onClick={onSave}>
        Сохранить как новую версию
      </Button>
    </div>
  </div>
);
